import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { Heart } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { NavBar } from "@/components/nav-bar";
import { Footer } from "@/components/footer";
import { motion } from "framer-motion";
import { AuthModal } from "@/components/auth-modal";
import {
  fadeIn,
  slideInFromBottom,
  staggerContainer,
  slideInFromLeft,
  slideInFromRight,
} from "@/lib/animations";
import { useState, useEffect } from "react";

export default function Home() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);
  const [hasExistingQuiz, setHasExistingQuiz] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Check if the user has existing quizzes
  useEffect(() => {
    async function checkExistingQuizzes() {
      if (user) {
        try {
          setIsLoading(true);
          const token = await user.getIdToken();
          const response = await fetch("/api/quiz/my", {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const quizzes = await response.json();
            setHasExistingQuiz(quizzes && quizzes.length > 0);
          }
        } catch (error) {
          console.error("Failed to check existing quizzes:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    }

    checkExistingQuizzes();
  }, [user]);

  // Opens the auth modal
  const handleOpenAuthModal = () => {
    setAuthModalOpen(true);
  };

  // Show the sign-in one-liner after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSignIn(true);
    }, 3000); // 3-second delay

    return () => clearTimeout(timer); // Cleanup on unmount
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation Bar */}
      <NavBar />

      {/* Hero Section with Background Image */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="relative pt-16 pb-20 md:pt-24 md:pb-28 lg:pt-32 lg:pb-36"
        style={{
          backgroundImage: "url('/images/backgrounds/wedding-toast.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: "brightness(0.9)",
        }}
      >
        {/* Gradient Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(219, 39, 119, 0.65) 0%, rgba(219, 39, 119, 0.45) 50%, rgba(255, 245, 247, 0.1) 100%)",
          }}
        ></div>

        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <motion.div
            className="max-w-3xl mx-auto text-center py-4"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={slideInFromBottom}>
              <Heart className="h-14 w-14 text-white mx-auto mb-6" />
            </motion.div>
            {/* Main Heading */}
            <motion.h1
              variants={slideInFromBottom}
              className="text-4xl md:text-5xl lg:text-6xl font-serif mb-6 leading-snug font-bold text-white"
              style={{
                textShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
              }}
            >
              Make Your Wedding Unforgettable with a Fun Quiz!
            </motion.h1>
            {/* Subheading */}
            <motion.p
              variants={slideInFromBottom}
              className="text-lg md:text-xl mb-12 max-w-2xl mx-auto font-medium text-white/90"
              style={{
                textShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              }}
            >
              Create a personalized quiz about you as a couple—your guests will
              laugh, connect, and leave with memories they'll cherish forever.
            </motion.p>
            ;{/* Primary CTA */}
            <motion.div
              variants={slideInFromBottom}
              whileHover={{
                scale: 1.05,
                transition: { duration: 0.2 },
              }}
            >
              <Button
                className="bg-white text-pink-700 hover:bg-pink-50 hover:text-pink-800 py-4 px-10 text-xl font-semibold transform transition-all hover:shadow-xl border border-pink-200"
                size="lg"
                onClick={() => setLocation("/create-quiz")}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5 text-pink-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Loading...
                  </span>
                ) : user && hasExistingQuiz ? (
                  "Continue Editing Your Quiz"
                ) : (
                  "Create Your Wedding Quiz Now!"
                )}
              </Button>
            </motion.div>
            {/* Secondary CTA - Always Render Container to Reserve Space */}
            <motion.div
              initial={{ opacity: 0, visibility: "hidden" }}
              animate={{
                opacity: showSignIn && !user ? 1 : 0,
                visibility: showSignIn && !user ? "visible" : "hidden",
              }}
              transition={{ duration: 0.5 }}
              className="mt-8"
              aria-live="polite"
              aria-hidden={!(showSignIn && !user)}
            >
              {user ? (
                // Placeholder when user is signed in to reserve space
                <div
                  className="text-lg md:text-xl mb-12 max-w-2xl mx-auto font-medium text-transparent"
                  style={{
                    textShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  Placeholder
                </div>
              ) : (
                // One-liner when user is not signed in
                <p
                  className="text-lg md:text-xl mb-12 max-w-2xl mx-auto font-medium text-white/90 inline-flex items-center gap-2"
                  style={{
                    textShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  Already have a quiz?{" "}
                  <Button
                    variant="outline"
                    className="bg-white text-gray-800 border-gray-300 hover:bg-gray-100 hover:border-gray-400 hover:text-pink-600 hover:underline gap-2 text-sm font-medium"
                    onClick={handleOpenAuthModal}
                    size="sm"
                  >
                    Sign in
                  </Button>{" "}
                  to manage it.
                </p>
              )}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>

      {/* Why Use Wedding Quiz Section */}
      <motion.div
        className="py-24 bg-gradient-to-b from-transparent to-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
      >
        <div className="container mx-auto px-4">
          <motion.h2
            className="text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 text-center mb-16"
            variants={slideInFromBottom}
          >
            Why Your Wedding Needs a Quiz
          </motion.h2>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-10 max-w-6xl mx-auto"
            variants={staggerContainer}
          >
            {/* Benefit 1 */}
            <motion.div
              className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300"
              variants={slideInFromLeft}
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
            >
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-pink-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="7" r="4" />
                  <path d="M12 11v4" />
                  <path d="M5 19a7 7 0 0 1 14 0" />
                  <circle cx="5" cy="7" r="2" />
                  <circle cx="19" cy="7" r="2" />
                  <path d="M5 9v2" />
                  <path d="M19 9v2" />
                </svg>
              </div>
              <h3 className="text-xl md:text-2xl font-serif text-gray-900 text-center mb-4">
                Engage Your Guests
              </h3>
              <p className="text-gray-600 text-center text-base">
                Spark laughter and connection with a fun activity that brings
                everyone together.
              </p>
            </motion.div>

            {/* Benefit 2 */}
            <motion.div
              className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300"
              variants={slideInFromBottom}
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
            >
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-pink-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z" />
                </svg>
              </div>
              <h3 className="text-xl md:text-2xl font-serif text-gray-900 text-center mb-4">
                Create Lasting Memories
              </h3>
              <p className="text-gray-600 text-center text-base">
                Leave your guests with a joyful experience they'll always
                remember.
              </p>
            </motion.div>

            {/* Benefit 3 */}
            <motion.div
              className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300"
              variants={slideInFromRight}
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
            >
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-pink-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                  <path
                    d="M9 9a3 3 0 0 1 3-3c1.5 0 3 1.5 3 3 0-1.5 1.5-3 3-3s3 1.5 3 3c0 3-6 6-6 6s-6-3-6-6z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <h3 className="text-xl md:text-2xl font-serif text-gray-900 text-center mb-4">
                Showcase Your Love Story
              </h3>
              <p className="text-gray-600 text-center text-base">
                Celebrate your unique journey with questions that delight your
                guests.
              </p>
            </motion.div>
          </motion.div>

          {/* Pricing CTA */}
          <motion.div
            className="mt-16 text-center"
            variants={slideInFromBottom}
          >
            <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h3>
            <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
              Create unlimited quizzes with our Free plan or upgrade for
              unlimited responses.
            </p>
            <Button
              onClick={() => setLocation("/pricing")}
              variant="outline"
              className="border-pink-400 text-pink-600 hover:bg-pink-50 px-8 py-4 text-lg"
            >
              View Pricing Plans
            </Button>
          </motion.div>
        </div>
      </motion.div>

      <Footer />

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </div>
  );
}
